import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import TopBar from './TopBar.jsx';
import Footer from './Footer.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';
import { HelpTourProvider } from './HelpTour.jsx';
import { UserModeProvider } from '../lib/user-mode.jsx';
import InstallBanner from './InstallBanner.jsx';
import RuntimeBanners from './RuntimeBanners.jsx';
import ImpersonationBanner from './ImpersonationBanner.jsx';
import AdminCmdK from './AdminCmdK.jsx';
import DemoModeBanner from './DemoModeBanner.jsx';

export default function Layout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  // Close drawer when route changes
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Escape closes the mobile nav drawer. Without this, keyboard users
  // had to tab back to the backdrop and Enter on it to close.
  useEffect(() => {
    if (!mobileNavOpen) return;
    function onKey(e) { if (e.key === 'Escape') setMobileNavOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  return (
    <UserModeProvider>
    <HelpTourProvider>
      {/* Skip-to-content link — invisible until focused. WCAG 2.4.1 (A).
          Keyboard users land here on tab-from-URL and can jump straight
          to main content instead of tabbing through 30 sidebar links + TopBar. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-[var(--color-brand-700,#0369a1)] focus:text-white focus:px-3 focus:py-2 focus:rounded focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>
      <div className="min-h-screen flex flex-col bg-[var(--color-bg-page)]">
      {/* id used by ToastCenter to measure real banner height — these wrap to
          multiple lines on mobile, so a hardcoded toast offset would overlap them. */}
      <div id="app-top-banners">
        <DemoModeBanner />
        <ImpersonationBanner />
      </div>
      <AdminCmdK />
      <div className="flex flex-1 min-h-0">
        <div className="hidden md:flex">
          <Sidebar />
        </div>

        {mobileNavOpen && (
          <>
            <button
              type="button"
              aria-label="Close navigation menu"
              className="md:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm cursor-pointer"
              onClick={() => setMobileNavOpen(false)}
            />
            <div className="md:hidden fixed inset-y-0 left-0 z-50 w-64" role="dialog" aria-modal="true" aria-label="Navigation">
              <Sidebar onNavigate={() => setMobileNavOpen(false)} />
            </div>
          </>
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <RuntimeBanners />
          <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />
          <main id="main-content" tabIndex="-1" className="flex-1 px-4 sm:px-6 py-6 overflow-x-auto">
            {/* Per-route boundary keyed by pathname: a single page crash shows
                the fallback in the content area while the sidebar/topbar stay
                usable, and navigating to any other route remounts a fresh
                boundary (resets hasError) so one bad page never poisons the
                rest of the session. */}
            <ErrorBoundary key={location.pathname} scope="route">
              <Outlet />
            </ErrorBoundary>
          </main>
          <Footer />
        </div>
        <InstallBanner />
      </div>
      </div>
    </HelpTourProvider>
    </UserModeProvider>
  );
}
